import React, { useEffect, useState } from "react";
import { Container, Row } from "react-bootstrap";
import { Button } from "primereact/button";
import { Carousel } from "primereact/carousel";
import { GiTwoCoins } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import CoinCheckoutSidebar from "../components/CoinCheckoutSidebar";
import SEO from "../components/SEO";
import "../assets/css/Pricing.css";

const responsiveOptions = [
  { breakpoint: "1200px", numVisible: 4, numScroll: 1 },
  { breakpoint: "992px", numVisible: 2, numScroll: 1 },
  { breakpoint: "576px", numVisible: 1, numScroll: 1 },
];

const Coinbuy = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [coinPacks, setCoinPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  /* =========================
     FETCH COIN PACKS
  ========================= */

  useEffect(() => {
    const fetchCoinPacks = async () => {
      try {
        const res = await api.get("/addcoinrts/coin_packs");

        if (res.data.success) {
          const formatted = res.data.data.map((pack) => ({
            id: pack.AddCoin_id,

            name: pack.pack_name,

            credits: pack.coin_amount,

            price: Number(pack.pack_value),

            originalPrice: pack.original_pack_value
              ? Number(pack.original_pack_value)
              : null,

            hasDiscount: Boolean(pack.has_discount),

            discountPercentage: pack.discount_percentage
              ? Number(pack.discount_percentage)
              : null,

            tierLevel: Number(pack.tier_level),

            tierName:
              pack.tier_name ||
              {
                1: "Basic",
                2: "Premium",
                3: "Plus",
                4: "Pro",
              }[Number(pack.tier_level)] ||
              "Unknown",

            isActive: Boolean(pack.is_active),
          }));

          const activePacks = formatted.filter(
            (pack) => pack.isActive
          );

          setCoinPacks(activePacks);

          if (activePacks.length) {
            setSelectedPack(
              activePacks[
                Math.floor(activePacks.length / 2)
              ]
            );
          }
        }
      } catch (err) {
        console.error(
          "Fetch coin packs failed:",
          err
        );
      } finally {
        setLoadingPacks(false);
      }
    };

    fetchCoinPacks();
  }, []);

  /* =========================
     SAME CARD AS PACKAGES PAGE
  ========================= */

  const packTemplate = (pack) => {
    return (
      <div
        className={`credit-card overflow-hidden ${
          selectedPack?.id === pack.id
            ? "active"
            : ""
        }`}
        onClick={() => setSelectedPack(pack)}
        style={{
          cursor: "pointer",
          margin: "0 8px",
        }}
      >
        {!isAuthenticated && (
          <div className="coin-tier-badge">
            {pack.tierName}
          </div>
        )}

        {pack.hasDiscount &&
          pack.discountPercentage && (
            <div className="offerBadge">
              {pack.discountPercentage}% OFF
            </div>
          )}

        <h2>
          <GiTwoCoins />{" "}
          {Number(pack.credits).toLocaleString(
            "en-IN"
          )}
        </h2>

        <h4
          className="price d-flex flex-column align-items-center"
          style={{
            letterSpacing: "normal",
          }}
        >
          {pack.hasDiscount &&
          pack.originalPrice ? (
            <>
              <span className="original-price">
                {pack.originalPrice.toLocaleString(
                  "en-IN"
                )}{" "}
                INR
              </span>

              <span className="discounted-price">
                {pack.price.toLocaleString(
                  "en-IN"
                )}{" "}
                INR
              </span>
            </>
          ) : (
            <span className="discounted-price">
              {pack.price.toLocaleString(
                "en-IN"
              )}{" "}
              INR
            </span>
          )}
        </h4>

        <small>{pack.name}</small>
      </div>
    );
  };

  /* =========================
     PAGE
  ========================= */

  return (
    <>
      <SEO
        title="Buy Credits"
        description="Buy credits separately and unlock premium designs instantly."
        canonical="https://shaadimaterial.com/coinbuy"
        keywords="buy credits, coin packs, design credits, premium assets"
        image="https://shaadimaterial.com/logo192.png"
      />

      <Container className="py-5">
        <Row className="mx-0 py-5 justify-content-center text-center">
          <h2>
            Finished Already? Get Additional
            Credits to get what you desire - with
            Credit Packs .
          </h2>

          <div className="pt-1 text-center">
            <h5 className="fw-semibold text-secondary mb-5">
              Explore flexible credit packs and
              unlock premium designs instantly.
            </h5>

            <Row className="justify-content-center g-3">
              {loadingPacks ? (
                <h5 className="text-secondary">
                  Loading coin packs...
                </h5>
              ) : coinPacks.length > 0 ? (
                <Carousel
                  value={coinPacks}
                  numVisible={4}
                  numScroll={1}
                  responsiveOptions={
                    responsiveOptions
                  }
                  itemTemplate={packTemplate}
                  circular
                  showIndicators={false}
                  showNavigators
                  className="credit-carousel"
                />
              ) : (
                <h5 className="text-secondary">
                  No credit packs available.
                </h5>
              )}
            </Row>

            <Button
              icon="pi pi-shopping-cart"
              label={
                selectedPack
                  ? `Buy Now for ${selectedPack.price.toLocaleString(
                      "en-IN"
                    )} INR`
                  : "Select a coin pack"
              }
              className="mt-5 pricing-subscribe-button"
              style={{
                fontSize: "18px",
              }}
              disabled={!selectedPack}
              tooltip={
                !isAuthenticated
                  ? "Login required to complete purchase."
                  : undefined
              }
              tooltipOptions={{
                position: "top",
              }}
              onClick={() => {
                if (!selectedPack) return;

                if (!isAuthenticated) {
                  navigate("/login", {
                    state: {
                      redirectTo: "/coinbuy",
                    },
                  });

                  return;
                }

                setCheckoutVisible(true);
              }}
            />

            <div className="cdpack_info">
              <p className="d-flex flex-column gap-0 mt-4 mb-0">
                <span>
                  Credits are valid for 1 year
                  from the date of purchase.
                </span>

                <span>
                  No auto-renewals — buy more
                  credits only when you need them.
                </span>

                <span>
                  Use credits to access our full
                  design library, including premium
                  templates.
                </span>

                <span>
                  Credit usage may vary depending
                  on asset type.
                </span>
              </p>
            </div>
          </div>
        </Row>
      </Container>

      <CoinCheckoutSidebar
        visible={checkoutVisible}
        onHide={() =>
          setCheckoutVisible(false)
        }
        pack={selectedPack}
        isAuthenticated={isAuthenticated}
      />
    </>
  );
};

export default Coinbuy;